// ABOUTME: Zod schemas for validating CAS import data documents
// ABOUTME: Provides type-safe validation for import data, offers, and courses

import { z } from 'zod';
import { ImportSource, FeeStatus } from '../index.js';

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Date format validation (YYYY-MM-DD)
const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Helper to create a date string schema that validates format and parses date
 */
const dateStringSchema = (fieldName: string) =>
  z
    .string()
    .regex(dateRegex, `${fieldName} must be in YYYY-MM-DD format`)
    .refine(date => {
      const parsed = new Date(date);
      return !isNaN(parsed.getTime());
    }, `Invalid ${fieldName}`)
    .optional();

// Constants for validation
const MAX_STRING_LENGTH = 1000;
// const CONDITION_FIELDS_COUNT = 5;
const PERCENTAGE_MAX = 100;

/**
 * Schema for sanitizing and validating strings
 */
const sanitizedStringSchema = z
  .string()
  .transform(val => val.trim())
  // eslint-disable-next-line no-control-regex
  .transform(val => val.replace(/[\x00-\x1F\x7F]/g, '')) // Remove control characters
  .transform(val => val.substring(0, MAX_STRING_LENGTH)) // Limit length
  .transform(val => val || undefined);

/**
 * Schema for phone numbers
 */
const phoneNumberSchema = z
  .string()
  .transform(val => val.replace(/[^\d+-]/g, '')) // Keep only digits, + and -
  .transform(val => val || undefined)
  .optional();

/**
 * Schema for import course data
 */
export const ImportCourseSchema = z
  .object({
    title: z.string().min(1, 'Course title is required'),
    level: sanitizedStringSchema.optional(),
    conditional: z.boolean().optional(),
    startDate: dateStringSchema('Start date').optional(),
    endDate: dateStringSchema('End date').optional(),
    isFullTime: z.boolean().optional(),
    hoursPerWeek: z.number().min(0).optional(),
    isATASRequired: z.boolean().optional(),
    courseFeeFirstYear: z.number().min(0, 'Course fee cannot be negative').optional(),
    courseScholarshipTitle1: sanitizedStringSchema.optional(),
    courseScholarshipAmount1: z.number().min(0).optional(),
    coursesAccepted: z.boolean().optional(),

    // Conditional offer fields
    conditionText1: sanitizedStringSchema.optional(),
    conditionText2: sanitizedStringSchema.optional(),
    conditionText3: sanitizedStringSchema.optional(),
    conditionText4: sanitizedStringSchema.optional(),
    conditionText5: sanitizedStringSchema.optional(),

    // Study address fields
    studyAddressLine1: sanitizedStringSchema.optional(),
    studyAddressLine2: sanitizedStringSchema.optional(),
    studyCity: sanitizedStringSchema.optional(),
    studyCountry: sanitizedStringSchema.optional(),
    studyPostcode: sanitizedStringSchema.optional(),

    // Work placement fields
    isWorkPlacement: z.boolean().optional(),
    workPlacementPerc: z.number().min(0).max(PERCENTAGE_MAX).optional(),
    workPlacementText: sanitizedStringSchema.optional(),
  })
  .superRefine((course, ctx) => {
    // Validate date order
    if (course.startDate && course.endDate) {
      const start = new Date(course.startDate);
      const end = new Date(course.endDate);
      if (start >= end) {
        ctx.addIssue({
          code: 'custom',
          path: ['endDate'],
          message: 'End date must be after start date',
        });
      }
    }

    // Validate conditional offer has conditions
    // if (course.conditional) {
    //   const hasConditions = Array.from({ length: CONDITION_FIELDS_COUNT }, (_, i) => i + 1).some(
    //     i => course[`conditionText${i}` as keyof typeof course]
    //   );
    //   if (!hasConditions) {
    //     ctx.addIssue({
    //       code: 'custom',
    //       message: 'Conditional offer must have at least one condition text',
    //       path: ['conditional']
    //     });
    //   }
    // }

    // Validate scholarship consistency
    if (course.courseScholarshipTitle1 && !course.courseScholarshipAmount1) {
      ctx.addIssue({
        code: 'custom',
        path: ['courseScholarshipAmount1'],
        message: `Scholarship amount required for "${course.courseScholarshipTitle1}"`,
      });
    }
  });

/**
 * Schema for import offer data
 */
export const ImportOfferSchema = z
  .object({
    intakeId: z.string().min(1, 'Intake ID is required'),
    offerId: z.string().min(1, 'Offer ID is required'),
    courses: z.array(ImportCourseSchema).min(1, 'At least one course is required'),

    // Optional fields
    withAgent: z.boolean().optional(),
    agentName: sanitizedStringSchema.optional(),
    agentCompany: sanitizedStringSchema.optional(),
    scholarshipTitle: sanitizedStringSchema.optional(),
    scholarshipAmount: z.number().min(0).optional(),
    courseCode: sanitizedStringSchema.optional(),
    courseId: sanitizedStringSchema.optional(),
    courseName: sanitizedStringSchema.optional(),
  })
  .superRefine((offer, ctx) => {
    // Validate agent consistency
    if (offer.withAgent && !offer.agentName) {
      ctx.addIssue({
        code: 'custom',
        path: ['agentName'],
        message: 'Agent name required when withAgent is true',
      });
    }

    // Validate scholarship consistency
    if (offer.scholarshipTitle && !offer.scholarshipAmount) {
      ctx.addIssue({
        code: 'custom',
        path: ['scholarshipAmount'],
        message: `Scholarship amount required for "${offer.scholarshipTitle}"`,
      });
    }
  });

/**
 * Schema for import data document
 */
export const ImportDataSchema = z.object({
  // Required fields
  studentNo: z.string({ required_error: 'Student number is required' }).min(1, 'Student number is required'),
  email: z.string({ required_error: 'Email is required' }).regex(emailRegex, 'Invalid email format'),
  familyName: z.string({ required_error: 'Family name is required' }).min(1, 'Family name is required'),
  givenName: z.string({ required_error: 'Given name is required' }).min(1, 'Given name is required'),

  // Optional fields
  uni: z.string().optional(),
  source: z.nativeEnum(ImportSource).optional(),
  otherName: sanitizedStringSchema.optional(),
  dob: dateStringSchema('Date of birth').optional(),
  phone: phoneNumberSchema,
  nationality: sanitizedStringSchema.optional(),
  placeOfBirth: sanitizedStringSchema.optional(),
  countryOfBirth: sanitizedStringSchema.optional(),
  gender: sanitizedStringSchema.optional(),

  // Address fields
  addressLine1: sanitizedStringSchema.optional(),
  addressLine2: sanitizedStringSchema.optional(),
  addressLine3: sanitizedStringSchema.optional(),
  city: sanitizedStringSchema.optional(),
  country: sanitizedStringSchema.optional(),
  postcode: sanitizedStringSchema.optional(),

  // Passport fields
  passportNumber: sanitizedStringSchema.optional(),
  passportExpiry: dateStringSchema('Passport expiry').optional(),

  // Academic fields
  ucasId: sanitizedStringSchema.optional(),
  applicationId: sanitizedStringSchema.optional(),
  englishLevelAttained: sanitizedStringSchema.optional(),
  englishTestProvider: sanitizedStringSchema.optional(),
  prevUKStudy: z.boolean().optional(),
  criminalConvictions: z.boolean().optional(),

  // Agent fields
  withAgent: z.boolean().optional(),
  agentName: sanitizedStringSchema.optional(),
  agentCompany: sanitizedStringSchema.optional(),

  // Offers
  offers: z.array(ImportOfferSchema).default([]),
});

/**
 * Schema for validating applicant details
 */
export const ApplicantDetailsValidationSchema = z.object({
  email: z.string().optional(),
  passport: z
    .object({
      passportNo: z.string().optional(),
      dateOfExpiry: z.string().optional(),
      dateOfIssue: z.string().optional(),
      passportIssuingAuthority: z.string().optional(),
    })
    .optional()
    .nullable(),
  englishTest: z
    .object({
      type: z.string().optional(),
      typeOther: z.string().optional(),
      score: z.string().optional(),
      date: z.string().optional(),
      referenceNo: z.string().optional(),
      validUntil: z.string().optional(),
    })
    .optional()
    .nullable(),
  reasonNotReqd: z.string().optional().nullable(),
  ukStudentVisas: z
    .array(
      z.object({
        type: z.string().optional(),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
      }),
    )
    .optional()
    .nullable(),
});

/**
 * Schema for validating application data
 */
export const ApplicationValidationSchema = z.object({
  userId: z.string(),
  applicationId: z.string(),
  intakeId: z.string().optional(),
  email: z.string().email('Invalid email format'),
  familyName: z.string().min(1, 'Family name is required'),
  givenName: z.string().min(1, 'Given name is required'),
  otherNames: z
    .array(
      z.object({
        givenName: z.string().optional(),
        familyName: z.string().optional(),
      }),
    )
    .optional()
    .nullable(),
  dob: z.string().optional(),
  phoneNumber: z.string().optional(),
  nationality: z.string().optional(),
  countryOfBirth: z.string().optional(),
  gender: z.string().optional(),
  contactAddress: z
    .object({
      addressLine1: z.string().optional(),
      addressLine2: z.string().optional(),
      city: z.string().optional(),
      postcode: z.string().optional(),
      country: z.string().optional(),
    })
    .optional()
    .nullable(),
  ucasId: z.string().optional(),
  criminalConvictions: z.boolean().optional(),
  hasAgent: z.string().optional(),
  agent: z.string().optional().nullable(),
  feeStatus: z.nativeEnum(FeeStatus),
});

/**
 * Combined validation schema for required documents
 */
export const RequiredDocumentsSchema = z.object({
  application: ApplicationValidationSchema,
  applicantDetails: ApplicantDetailsValidationSchema.nullable(),
});

/**
 * Type exports
 */
export type ImportDataValidation = z.infer<typeof ImportDataSchema>;
export type ImportOfferValidation = z.infer<typeof ImportOfferSchema>;
export type ImportCourseValidation = z.infer<typeof ImportCourseSchema>;
export type RequiredDocumentsValidation = z.infer<typeof RequiredDocumentsSchema>;
